import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export interface BannerSlide {
  id: string;
  title: string;
  subtitle: string;
  button_text: string;
  button_link: string;
  show_button: boolean;
  image_url: string | null;
  sort_order: number;
  is_active: boolean;
  target_audience: 'user' | 'partner';
}

export function useBannerSlides(audience?: 'user' | 'partner') {
  const queryClient = useQueryClient();

  const { data: slides = [], isLoading: loading, refetch: fetchSlides } = useQuery({
    queryKey: ['banner_slides', audience],
    queryFn: async () => {
      let query = supabase.from('banner_slides').select('*').order('sort_order');
      if (audience) {
        query = query.eq('target_audience', audience);
      }
      const { data } = await query;
      return (data || []) as any[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: slideInterval = 2000 } = useQuery({
    queryKey: ['banner_slide_interval'],
    queryFn: async () => {
      const { data } = await supabase.from('app_settings').select('value').eq('key', 'banner_slide_interval').maybeSingle();
      return data ? Number(data.value) || 2000 : 2000;
    },
    staleTime: 5 * 60 * 1000,
  });

  const updateInterval = async (ms: number) => {
    const { data: existing } = await supabase.from('app_settings').select('id').eq('key', 'banner_slide_interval').maybeSingle();
    if (existing) {
      await supabase.from('app_settings').update({ value: String(ms) }).eq('key', 'banner_slide_interval');
    } else {
      await supabase.from('app_settings').insert({ key: 'banner_slide_interval', value: String(ms) });
    }
    queryClient.invalidateQueries({ queryKey: ['banner_slide_interval'] });
  };

  const addSlide = async (slide: Partial<BannerSlide>) => {
    const maxOrder = slides.length > 0 ? Math.max(...slides.map(s => s.sort_order)) + 1 : 0;
    const { data, error } = await supabase
      .from('banner_slides')
      .insert({ ...slide, sort_order: maxOrder } as any)
      .select()
      .single();
    if (!error) queryClient.invalidateQueries({ queryKey: ['banner_slides'] });
    return { data, error };
  };

  const updateSlide = async (id: string, updates: Partial<BannerSlide>) => {
    const { error } = await supabase
      .from('banner_slides')
      .update({ ...updates, updated_at: new Date().toISOString() } as any)
      .eq('id', id);
    if (!error) queryClient.invalidateQueries({ queryKey: ['banner_slides'] });
    return { error };
  };

  const deleteSlide = async (id: string) => {
    const { error } = await supabase.from('banner_slides').delete().eq('id', id);
    if (!error) queryClient.invalidateQueries({ queryKey: ['banner_slides'] });
    return { error };
  };

  const uploadSlideImage = async (id: string, file: File): Promise<string> => {
    const { uploadToImageKit } = await import('@/utils/imagekit');
    const url = await uploadToImageKit(file);
    await updateSlide(id, { image_url: url } as any);
    return url;
  };

  const activeSlides = slides.filter(s => s.is_active);

  return { slides, activeSlides, loading, slideInterval, updateInterval, addSlide, updateSlide, deleteSlide, uploadSlideImage, refetch: fetchSlides };
}
